import { Component } from '@theme/component';

/**
 * Aligns comparison metafield (spec) rows across product columns.
 *
 * Parity with the Salomon compare section: a spec row is shown only when EVERY
 * compared product has a non-empty value for that metafield. If any product is
 * missing it, the row is hidden across all columns so the rows stay aligned.
 *
 * Progressive enhancement: the server already hides empty cells via the `hidden`
 * attribute (see `compare-product-metafield-row`), so without JS the comparison is
 * still readable — this component only tidies up the cross-column alignment.
 *
 * Rows are matched across columns by position. Every column renders the same
 * `_product-card` static block, so the Nth metafield row is the same block in every
 * column (the mobile carousel's second copy of the columns included). The row markup
 * carries nothing else that is stable across columns: the rendered value differs per
 * product, and Shopify suffixes the block id per repeated static-block render (`-1`).
 *
 * Runs once on connect. Skipped in the theme editor (`Shopify.designMode`) so the
 * merchant keeps seeing every configured row (with placeholders) while editing.
 *
 * @extends {Component}
 */
export class CompareMetafieldsComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    const columns = this.querySelectorAll('[data-compare-column]');
    if (columns.length < 2) {
      if (window.Shopify && window.Shopify.designMode) {
        this.dataset.aligned = 'design-mode';
      }
      return;
    }

    // Always equalize product card heights for perfect layout, even in theme editor
    this.#applyHeightEqualization(columns);

    if (window.Shopify && window.Shopify.designMode) {
      this.dataset.aligned = 'design-mode';
      return;
    }

    this.#align(columns);
  }

  #applyHeightEqualization(columns) {
    this.#equalizeHeights(columns);

    if (!this.resizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.#equalizeHeights(columns);
      });
      for (const column of columns) {
        const card = column.querySelector('.product-card');
        if (card) {
          this.resizeObserver.observe(card);
        }
      }
    }
  }

  #align(columns) {
    const rowsByColumn = Array.from(columns, (column) =>
      Array.from(column.querySelectorAll('.compare-products-metafield'))
    );
    const rowCount = Math.max(...rowsByColumn.map((rows) => rows.length));

    // Row N is kept only when every column has it and none of them is empty.
    const keepByIndex = Array.from({ length: rowCount }, (_, index) =>
      rowsByColumn.every((rows) => rows[index] && rows[index].dataset.isEmpty !== 'true')
    );

    for (const rows of rowsByColumn) {
      let visibleIndex = 0;
      for (const [index, row] of rows.entries()) {
        const keep = keepByIndex[index];
        row.hidden = !keep;

        if (keep) {
          visibleIndex++;
          if (visibleIndex % 2 === 0) {
            row.classList.add('compare-products-metafield--even');
          } else {
            row.classList.remove('compare-products-metafield--even');
          }
        } else {
          row.classList.remove('compare-products-metafield--even');
        }
      }
    }
    this.dataset.aligned = 'true';
  }

  disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  #equalizeHeights(columns) {
    if (!this.groupsCreated) {
      for (const column of columns) {
        const content = column.querySelector('.product-card__content');
        if (!content) continue;

        // Wrap top section
        const topSection = document.createElement('div');
        topSection.className = 'compare-products__top-section';
        topSection.style.display = 'flex';
        topSection.style.flexDirection = 'column';
        // inherit gap directly for consistency
        topSection.style.rowGap = getComputedStyle(content).rowGap || '8px';

        // Wrap metafields section
        const metaSection = document.createElement('div');
        metaSection.className = 'compare-products__metafields-section';
        metaSection.style.display = 'flex';
        metaSection.style.flexDirection = 'column';
        
        // Ensure any continuous background logic maps inside this wrapper natively
        metaSection.style.width = '100%';

        // Move children recursively (excluding slots/widgets added implicitly outside product-card__content)
        const children = Array.from(content.children);
        for (const child of children) {
          const isPrice = child.classList.contains('price') || child.tagName.toLowerCase() === 'product-price' || child.querySelector('product-price');
          if (child.classList.contains('compare-products-metafield') || isPrice) {
            metaSection.appendChild(child);
          } else {
            topSection.appendChild(child);
          }
        }

        content.appendChild(topSection);
        content.appendChild(metaSection);
      }
      this.groupsCreated = true;
    }

    let maxTopHeight = 0;
    const topSections = [];

    for (const column of columns) {
      const topSection = column.querySelector('.compare-products__top-section');
      if (topSection) {
        topSection.style.height = 'auto'; // Reset for natural measurement
        topSections.push({ column, topSection });
      }
    }

    // Only measure from non-label (visible) columns to capture actual visual dimensions
    for (const item of topSections) {
      const { column, topSection } = item;
      if (!column.closest('.compare-similar-products__item--labels, .compare-products__item--labels') && topSection.offsetWidth > 0) {
        maxTopHeight = Math.max(maxTopHeight, topSection.offsetHeight);
      }
    }

    if (maxTopHeight > 0) {
      for (const item of topSections) {
        item.topSection.style.height = `${maxTopHeight}px`;
      }
    }

    // Equalize individual metafield rows across all columns by index
    const allMetaSections = Array.from(columns).map(col => col.querySelector('.compare-products__metafields-section')).filter(Boolean);
    if (allMetaSections.length === columns.length && allMetaSections.length > 0) {
      const rowCount = allMetaSections[0].children.length;
      
      // 1. Reset heights to compute natural text-wrapped height
      for (let i = 0; i < rowCount; i++) {
        for (const metaSection of allMetaSections) {
          const row = metaSection.children[i];
          if (row) row.style.height = 'auto';
        }
      }

      // 2. Compute max height for each synchronized row
      for (let i = 0; i < rowCount; i++) {
        let maxRowHeight = 0;
        for (const metaSection of allMetaSections) {
          const row = metaSection.children[i];
          if (row && !row.hidden && window.getComputedStyle(row).display !== 'none') {
            maxRowHeight = Math.max(maxRowHeight, row.offsetHeight);
          }
        }
        
        // 3. Apply the maximum bound box height across all related items 
        if (maxRowHeight > 0) {
          for (const metaSection of allMetaSections) {
            const row = metaSection.children[i];
            if (row) {
              row.style.height = `${maxRowHeight}px`;
              // Also ensure inner flex containers stretch centrally if needed
              row.style.justifyContent = 'center';
            }
          }
        }
      }
    }
  }
}

if (!customElements.get('compare-metafields-component')) {
  customElements.define('compare-metafields-component', CompareMetafieldsComponent);
}
